import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { Roles, RoleLabels } from '../auth/rbac'
import { useDebouncedValue } from '../hooks/useDebouncedValue'
import InlineError from '../ui/InlineError'
import Loading from '../ui/Loading'
import { PrivacyContent, TermsContent } from './legal/LegalContents'

export default function SignupPage() {
  const navigate = useNavigate()
  const [orgOptions, setOrgOptions] = useState([])
  const [roleOptions, setRoleOptions] = useState([])
  const [legal, setLegal] = useState({
    termsVersion: '2026-01-12',
    privacyVersion: '2026-01-12',
    marketingVersion: '2026-01-12',
    termsPath: '/terms',
    privacyPath: '/privacy',
  })
  const [form, setForm] = useState({
    email: '',
    name: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    orgId: '',
    requestedRole: ''
  })
  const [agreements, setAgreements] = useState({
    terms: false,
    privacy: false,
    marketing: false,
  })
  const [legalModal, setLegalModal] = useState({ open: false, doc: null }) // doc: 'terms' | 'privacy'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [orgCategory, setOrgCategory] = useState('')
  const [orgL1, setOrgL1] = useState('')
  const [orgL2, setOrgL2] = useState('')
  const [orgL3, setOrgL3] = useState('')

  const [emailCheck, setEmailCheck] = useState({
    status: 'idle', // idle | invalid | checking | available | unavailable | error
    checkedEmail: '',
    message: null,
  })

  const isValidEmail = (v) => {
    if (!v) return false
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v).trim())
  }

  const checkEmailNow = async (rawEmail) => {
    const email = String(rawEmail || '').trim()
    if (!email) {
      const next = { status: 'invalid', checkedEmail: '', message: '이메일을 입력해 주세요.' }
      setEmailCheck(next)
      return { available: false, message: next.message }
    }
    if (!isValidEmail(email)) {
      const next = { status: 'invalid', checkedEmail: email, message: '이메일 형식을 확인해 주세요.' }
      setEmailCheck(next)
      return { available: false, message: next.message }
    }

    // 이미 확인된 값이면 그대로 반환
    if (emailCheck.checkedEmail === email && emailCheck.status === 'available') {
      return { available: true }
    }
    if (emailCheck.checkedEmail === email && emailCheck.status === 'unavailable') {
      return { available: false, message: emailCheck.message || '이미 사용 중인 이메일입니다.' }
    }

    setEmailCheck(prev => ({ ...prev, status: 'checking', checkedEmail: email, message: null }))
    try {
      const res = await api.get('/signups/public/check-email', { params: { email } })
      const payload = res?.data?.data ?? res?.data
      const available = Boolean(payload?.available)
      const reason = payload?.reason
      const next = available
        ? { status: 'available', checkedEmail: email, message: '사용 가능한 이메일입니다.' }
        : { status: 'unavailable', checkedEmail: email, message: reason || '이미 사용 중인 이메일입니다.' }
      setEmailCheck(next)
      return { available, message: next.message }
    } catch (err) {
      const next = { status: 'error', checkedEmail: email, message: '이메일 중복 확인에 실패했습니다.' }
      setEmailCheck(next)
      return { available: false, message: next.message }
    }
  }

  const debouncedEmail = useDebouncedValue(form.email, 400)

  // 입력 중에도 자동으로(디바운스) 중복 확인하여 사용자 경험 개선
  useEffect(() => {
    const email = String(debouncedEmail || '').trim()
    if (!email) {
      setEmailCheck({ status: 'idle', checkedEmail: '', message: null })
      return
    }
    if (!isValidEmail(email)) {
      setEmailCheck(prev => ({ ...prev, status: 'invalid', checkedEmail: email, message: '이메일 형식을 확인해 주세요.' }))
      return
    }
    if (emailCheck.checkedEmail === email && (emailCheck.status === 'available' || emailCheck.status === 'unavailable')) {
      return
    }

    let ignore = false
    ;(async () => {
      setEmailCheck(prev => ({ ...prev, status: 'checking', checkedEmail: email, message: null }))
      try {
        const res = await api.get('/signups/public/check-email', { params: { email } })
        const payload = res?.data?.data ?? res?.data
        const available = Boolean(payload?.available)
        const reason = payload?.reason
        const next = available
          ? { status: 'available', checkedEmail: email, message: '사용 가능한 이메일입니다.' }
          : { status: 'unavailable', checkedEmail: email, message: reason || '이미 사용 중인 이메일입니다.' }
        if (!ignore) setEmailCheck(next)
      } catch {
        if (!ignore) setEmailCheck({ status: 'error', checkedEmail: email, message: '이메일 중복 확인에 실패했습니다.' })
      }
    })()

    return () => { ignore = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEmail])

  // 최상위(생보/손보/GA)별 역할 그룹
  const gaRoleGroup = useMemo(
    () => [
      Roles.GA_DEPUTY_LEADER,
      Roles.GA_LEADER,
      Roles.GA_MANAGER,
      Roles.GA_PLANNER,
      Roles.GA_BRANCH_MANAGER,
      Roles.GA_DEPUTY_BRANCH_MANAGER,
      Roles.GA_OTHER_ADMIN,
      Roles.GA_ETC,
    ],
    []
  )

  const insurerRoleGroup = useMemo(
    () => [
      Roles.INSURER_MANAGER,
      Roles.INSURER_DEPUTY_LEADER,
      Roles.INSURER_LEADER,
      Roles.INSURER_BRANCH_MANAGER,
      Roles.INSURER_DEPUTY_BRANCH_MANAGER,
      Roles.INSURER_EDUCATION_MANAGER,
      Roles.INSURER_PLAN_MANAGER,
      Roles.INSURER_OTHER_ADMIN,
      Roles.INSURER_ETC,
    ],
    []
  )

  useEffect(() => {
    async function load() {
      try {
        const [orgRes, legalRes] = await Promise.all([
          api.get('/orgs/public-options'),
          api.get('/signups/public/legal').catch(() => null),
        ])
        const orgData = orgRes?.data?.data ?? orgRes?.data ?? []
        setOrgOptions(orgData)

        const legalPayload = legalRes?.data?.data ?? legalRes?.data ?? null
        if (legalPayload) {
          setLegal(prev => ({
            ...prev,
            termsVersion: legalPayload?.termsVersion || prev.termsVersion,
            privacyVersion: legalPayload?.privacyVersion || prev.privacyVersion,
            marketingVersion: legalPayload?.marketingVersion || prev.marketingVersion,
            termsPath: legalPayload?.termsPath || prev.termsPath,
            privacyPath: legalPayload?.privacyPath || prev.privacyPath,
          }))
        }

        // 직접 회원가입에서 선택 가능한 역할:
        // - OPERATOR 및 마스터(최상위) 관리자/부관리자 역할은 직접 신청 불가(운영/정책성 역할)
        const blocked = new Set([
          Roles.OPERATOR,
          Roles.GA_MASTER_ADMIN,
          Roles.GA_MASTER_DEPUTY_ADMIN,
          Roles.INSURER_MASTER_ADMIN,
          Roles.INSURER_MASTER_DEPUTY_ADMIN,
        ])
        const allowedCodes = Object.values(Roles).filter(code => !blocked.has(code))
        setRoleOptions(allowedCodes)
      } catch (e) {
        setError('조직/역할 정보를 불러오지 못했습니다.')
      }
    }
    load()
  }, [])

  const handleChange = (e) => {
    const { name, value } = e.target
    if (name === 'email') {
      // 이메일 변경 시 기존 중복확인 결과는 무효
      setEmailCheck(prev => ({ ...prev, status: 'idle', checkedEmail: '', message: null }))
    }
    setForm(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setSuccess(null)

    // 이메일 중복 확인(최종 방어선)
    const emailResult = await checkEmailNow(form.email)
    if (!emailResult.available) {
      setError(emailResult.message || '이메일 중복 확인에 실패했습니다.')
      return
    }

    if (form.password !== form.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.')
      return
    }
    if (form.password.length < 6) {
      setError('비밀번호는 6자 이상이어야 합니다.')
      return
    }
    if (!form.orgId || !form.requestedRole) {
      setError('조직과 역할을 선택해 주세요.')
      return
    }
    if (!agreements.terms || !agreements.privacy) {
      setError('이용약관 및 개인정보 처리방침 동의가 필요합니다.')
      return
    }

    setLoading(true)
    try {
      await api.post('/signups/public', {
        email: form.email,
        name: form.name,
        password: form.password,
        phoneNumber: form.phoneNumber,
        orgId: Number(form.orgId),
        requestedRole: form.requestedRole,
        termsAccepted: Boolean(agreements.terms),
        termsVersion: legal.termsVersion || 'unknown',
        privacyAccepted: Boolean(agreements.privacy),
        privacyVersion: legal.privacyVersion || 'unknown',
        marketingAccepted: Boolean(agreements.marketing),
        marketingVersion: legal.marketingVersion || null,
        consentChannel: 'WEB',
      })
      setSuccess('가입 신청이 접수되었습니다. 승인 후 로그인할 수 있습니다.')
      setTimeout(() => {
        navigate('/login')
      }, 1500)
    } catch (err) {
      const msg = err?.response?.data?.error?.message
        || err?.response?.data?.message
        || err?.message
        || '가입 신청에 실패했습니다.'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  const roleLabel = (code) => {
    return RoleLabels[code] || code
  }

  // 상단 조직 구분에 따라 신청 가능한 역할 필터링
  const filteredRoleOptions = useMemo(() => {
    if (!orgCategory) {
      return roleOptions
    }
    if (orgCategory === 'GA') {
      return roleOptions.filter(code => gaRoleGroup.includes(code))
    }
    if (orgCategory === 'INS_LIFE' || orgCategory === 'INS_NONLIFE') {
      return roleOptions.filter(code => insurerRoleGroup.includes(code))
    }
    return roleOptions
  }, [orgCategory, roleOptions, gaRoleGroup, insurerRoleGroup])

  const childrenOf = React.useCallback(
    (parentId) => orgOptions.filter(o => o.parentOrgId === parentId),
    [orgOptions]
  )

  const lifeRootIds = useMemo(
    () =>
      orgOptions
        .filter(o => !o.parentOrgId && o.type === 'INSURER' && (o.name.includes('생명') || o.name.includes('생보')))
        .map(o => o.id),
    [orgOptions]
  )

  const nonLifeRootIds = useMemo(
    () =>
      orgOptions
        .filter(o => !o.parentOrgId && o.type === 'INSURER' && (o.name.includes('손해') || o.name.includes('화재') || o.name.includes('해상')))
        .map(o => o.id),
    [orgOptions]
  )

  const gaRootIds = useMemo(
    () => orgOptions.filter(o => !o.parentOrgId && o.type === 'GA').map(o => o.id),
    [orgOptions]
  )

  const level1Options = useMemo(() => {
    if (!orgCategory) return []
    if (orgCategory === 'GA') {
      return orgOptions.filter(o => gaRootIds.includes(o.id))
    }
    if (orgCategory === 'INS_LIFE') {
      return orgOptions.filter(o => lifeRootIds.includes(o.parentOrgId))
    }
    if (orgCategory === 'INS_NONLIFE') {
      return orgOptions.filter(o => nonLifeRootIds.includes(o.parentOrgId))
    }
    return []
  }, [orgCategory, orgOptions, gaRootIds, lifeRootIds, nonLifeRootIds])

  const level2Options = useMemo(() => {
    if (!orgL1) return []
    return childrenOf(Number(orgL1))
  }, [orgL1, childrenOf])

  const level3Options = useMemo(() => {
    if (!orgL2) return []
    return childrenOf(Number(orgL2))
  }, [orgL2, childrenOf])

  // 최종 선택된 orgId 동기화 (가장 하위 선택값 우선)
  useEffect(() => {
    const selected =
      orgL3 || orgL2 || orgL1 || ''
    setForm(prev => ({ ...prev, orgId: selected ? Number(selected) : '' }))
  }, [orgL1, orgL2, orgL3])

  const handleCategoryChange = (e) => {
    const v = e.target.value
    setOrgCategory(v)
    setOrgL1('')
    setOrgL2('')
    setOrgL3('')

    // 선택 가능한 역할도 함께 갱신
    let nextRole = ''
    if (v === 'GA') {
      const candidates = roleOptions.filter(code => gaRoleGroup.includes(code))
      nextRole = candidates[0] || ''
    } else if (v === 'INS_LIFE' || v === 'INS_NONLIFE') {
      const candidates = roleOptions.filter(code => insurerRoleGroup.includes(code))
      nextRole = candidates[0] || ''
    } else {
      // 구분을 비운 경우 역할도 초기화
      nextRole = ''
    }

    setForm(prev => ({
      ...prev,
      orgId: '',
      requestedRole: nextRole,
    }))
  }

  const handleLevel1Change = (e) => {
    const v = e.target.value
    setOrgL1(v)
    setOrgL2('')
    setOrgL3('')
  }

  const handleLevel2Change = (e) => {
    const v = e.target.value
    setOrgL2(v)
    setOrgL3('')
  }

  const handleLevel3Change = (e) => {
    const v = e.target.value
    setOrgL3(v)
  }

  const requiredAgreed = agreements.terms && agreements.privacy
  const closeLegalModal = () => setLegalModal({ open: false, doc: null })

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      {legalModal.open && (
        <div className="fixed inset-0 z-[1000]">
          <div className="absolute inset-0 bg-black/40" onClick={closeLegalModal} />
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="w-full max-w-2xl rounded-lg bg-white shadow-xl border">
              <div className="flex items-center justify-between px-4 py-3 border-b">
                <div className="font-semibold text-slate-900">
                  {legalModal.doc === 'terms' ? '이용약관' : '개인정보 처리방침'}
                  <span className="ml-2 text-xs text-slate-500">
                    (버전: {legalModal.doc === 'terms' ? (legal.termsVersion || '-') : (legal.privacyVersion || '-')})
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <a
                    className="text-xs text-slate-600 hover:text-brand-sky"
                    href={legalModal.doc === 'terms' ? (legal.termsPath || '/terms') : (legal.privacyPath || '/privacy')}
                    target="_blank"
                    rel="noreferrer"
                  >
                    전체 화면
                  </a>
                  <button type="button" className="text-sm text-slate-600 hover:text-slate-900" onClick={closeLegalModal}>
                    닫기
                  </button>
                </div>
              </div>
              <div className="max-h-[70vh] overflow-auto px-4 py-4">
                {legalModal.doc === 'terms' ? <TermsContent /> : <PrivacyContent />}
              </div>
              <div className="px-4 py-3 border-t flex items-center justify-end gap-2">
                {legalModal.doc === 'terms' && (
                  <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                    onClick={() => {
                      setAgreements(prev => ({ ...prev, terms: true }))
                      closeLegalModal()
                    }}
                  >
                    이용약관 동의
                  </button>
                )}
                {legalModal.doc === 'privacy' && (
                  <button
                    type="button"
                    className="rounded border px-3 py-2 text-sm hover:bg-slate-50"
                    onClick={() => {
                      setAgreements(prev => ({ ...prev, privacy: true }))
                      closeLegalModal()
                    }}
                  >
                    개인정보 처리방침 동의
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            회원가입 신청
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            소속 조직과 역할을 선택하고 정보를 입력해 주세요. 승인 후 사용 가능합니다.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                이메일
              </label>
              <div className="mt-1 flex gap-2">
                <input
                  name="email"
                  type="email"
                  required
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                  placeholder="example@company.com"
                  value={form.email}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => checkEmailNow(form.email)}
                  disabled={loading || emailCheck.status === 'checking' || !isValidEmail(form.email)}
                  className="shrink-0 rounded border px-3 py-2 text-sm hover:bg-slate-50 disabled:opacity-50"
                >
                  {emailCheck.status === 'checking' ? '확인 중...' : '중복 확인'}
                </button>
              </div>
              {emailCheck.message && (
                <div
                  className={[
                    'mt-1 text-xs',
                    emailCheck.status === 'available' ? 'text-emerald-700' : '',
                    (emailCheck.status === 'unavailable' || emailCheck.status === 'invalid' || emailCheck.status === 'error') ? 'text-red-700' : '',
                    (emailCheck.status !== 'available' && emailCheck.status !== 'unavailable' && emailCheck.status !== 'invalid' && emailCheck.status !== 'error') ? 'text-slate-600' : '',
                  ].filter(Boolean).join(' ')}
                >
                  {emailCheck.message}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                이름
              </label>
              <input
                name="name"
                type="text"
                required
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                placeholder="홍길동"
                value={form.name}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                휴대폰 번호 (선택)
              </label>
              <input
                name="phoneNumber"
                type="tel"
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                placeholder="010-0000-0000"
                value={form.phoneNumber}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                소속 조직
              </label>
              <div className="grid grid-cols-1 gap-2">
                {/* 1단계: 카테고리 선택 (생보/손보/GA) */}
                <select
                  className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                  value={orgCategory}
                  onChange={handleCategoryChange}
                >
                  <option value="">구분 선택: 생보 / 손보 / GA</option>
                  {lifeRootIds.length > 0 && <option value="INS_LIFE">생보(생명보험)</option>}
                  {nonLifeRootIds.length > 0 && <option value="INS_NONLIFE">손보(손해보험)</option>}
                  {gaRootIds.length > 0 && <option value="GA">GA</option>}
                </select>

                {/* 2단계: 상위 조직 (보험사 / GA 본사 등) */}
                <select
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                  value={orgL1}
                  onChange={handleLevel1Change}
                  disabled={!orgCategory || level1Options.length === 0}
                >
                  <option value="">{orgCategory ? '상위 조직 선택' : '먼저 구분을 선택하세요'}</option>
                  {level1Options.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>

                {/* 3단계: 1차 하위 */}
                <select
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                  value={orgL2}
                  onChange={handleLevel2Change}
                  disabled={level2Options.length === 0}
                >
                  <option value="">1차 하위 조직 (선택 사항)</option>
                  {level2Options.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>

                {/* 4단계: 2차 하위 */}
                <select
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                  value={orgL3}
                  onChange={handleLevel3Change}
                  disabled={level3Options.length === 0}
                >
                  <option value="">2차 하위 조직 (선택 사항)</option>
                  {level3Options.map(o => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                신청 역할
              </label>
              <select
                name="requestedRole"
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                value={form.requestedRole}
                onChange={handleChange}
              >
                {filteredRoleOptions.map(code => (
                  <option key={code} value={code}>
                    {roleLabel(code)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                비밀번호
              </label>
              <input
                name="password"
                type="password"
                required
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                placeholder="6자 이상"
                value={form.password}
                onChange={handleChange}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">
                비밀번호 확인
              </label>
              <input
                name="confirmPassword"
                type="password"
                required
                className="mt-1 w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-brand-navy focus:border-brand-navy"
                placeholder="비밀번호를 다시 입력하세요"
                value={form.confirmPassword}
                onChange={handleChange}
              />
            </div>

            <div className="rounded-md border bg-white p-3">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-slate-800">약관 동의</div>
                <button
                  type="button"
                  className="text-xs text-slate-600 hover:text-brand-sky"
                  onClick={() => setAgreements({ terms: true, privacy: true, marketing: true })}
                >
                  전체 동의
                </button>
              </div>

              <div className="mt-3 space-y-2 text-sm">
                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={agreements.terms}
                    onChange={(e) => setAgreements(prev => ({ ...prev, terms: e.target.checked }))}
                  />
                  <span className="flex-1">
                    <span className="text-rose-600">[필수]</span> 이용약관 동의
                    <button
                      type="button"
                      className="ml-2 text-xs text-slate-600 hover:text-brand-sky underline"
                      onClick={() => setLegalModal({ open: true, doc: 'terms' })}
                    >
                      보기
                    </button>
                  </span>
                </label>

                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={agreements.privacy}
                    onChange={(e) => setAgreements(prev => ({ ...prev, privacy: e.target.checked }))}
                  />
                  <span className="flex-1">
                    <span className="text-rose-600">[필수]</span> 개인정보 처리방침 동의
                    <button
                      type="button"
                      className="ml-2 text-xs text-slate-600 hover:text-brand-sky underline"
                      onClick={() => setLegalModal({ open: true, doc: 'privacy' })}
                    >
                      보기
                    </button>
                  </span>
                </label>

                <label className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={agreements.marketing}
                    onChange={(e) => setAgreements(prev => ({ ...prev, marketing: e.target.checked }))}
                  />
                  <span className="flex-1">
                    [선택] 마케팅/광고성 정보 수신 동의
                  </span>
                </label>

                <div className="text-xs text-slate-500">
                  {requiredAgreed ? '필수 약관에 동의했습니다.' : '필수 약관(이용약관/개인정보 처리방침)에 동의해 주세요.'}
                  {legal.termsVersion || legal.privacyVersion ? (
                    <span className="ml-1">
                      (버전: {legal.termsVersion || '-'} / {legal.privacyVersion || '-'})
                    </span>
                  ) : null}
                </div>
                <div className="text-[11px] text-slate-400">선택 동의는 서비스 이용에 필수가 아닙니다.</div>
              </div>
            </div>
          </div>

          {error && <InlineError message={error} />}
          {success && <div className="text-sm text-green-700">{success}</div>}

          <div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-brand-navy hover:bg-brand-navy-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-navy disabled:opacity-50"
            >
              {loading ? <Loading label="신청 중..." /> : '가입 신청'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}


